import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { fetchPublicProject, fetchPublicArchiveLocations, fetchPublicLabels, fetchPublicProjectKmlData } from "../utils/api";
import { formatPin } from "../utils/pinFormat";
import MapContainer from "../features/map/components/MapContainer";
import ProjectDescriptionModal from "../components/ProjectDescriptionModal";
import PublicIntroAnimation from "../components/PublicIntroAnimation";
import Loading from "../features/uiStatus/components/Loading";
import { typeShell } from "../styles/typeShell";
import { publicMapShell } from "../styles/publicMapShell";

function PublicMapPage() {
    const { publicUrl } = useParams();
    const [project, setProject] = useState(null);
    const [archiveLocations, setArchiveLocations] = useState([]);
    const [labels, setLabels] = useState([]);
    const [kmlData, setKmlData] = useState(null);
    const [useKml, setUseKml] = useState(false);
    const [center, setCenter] = useState(null);
    const [zoom, setZoom] = useState(15);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDescriptionModal, setShowDescriptionModal] = useState(false);
    const [showIntroAnimation, setShowIntroAnimation] = useState(true);

    // 公開状態をチェックする関数
    const checkPublicStatus = useCallback(async () => {
        try {
            await fetchPublicProject(publicUrl);
            // 成功した場合は何もしない（まだ公開中）
        } catch (err) {
            // エラーが発生した場合は非公開になったと判断
            console.log("プロジェクトが非公開になりました");
            setError("このプロジェクトは非公開になりました。");
        }
    }, [publicUrl]);

    useEffect(() => {
        const loadPublicProject = async () => {
            try {
                setLoading(true);
                const projectData = await fetchPublicProject(publicUrl);
                setProject(projectData);

                if (projectData.latitude && projectData.longitude) {
                    setCenter({ lat: projectData.latitude, lng: projectData.longitude });
                }

                if (projectData.zoom !== undefined && projectData.zoom !== null) {
                    setZoom(projectData.zoom);
                }

                // ピン・ラベル・KMLを並行取得（エラーが出ても続行）
                let locationsData = [];
                let labelsData = [];

                try {
                    locationsData = await fetchPublicArchiveLocations(publicUrl);
                } catch (err) {
                    console.error("公開アーカイブロケーション取得エラー:", err);
                }

                try {
                    labelsData = await fetchPublicLabels(publicUrl);
                } catch (err) {
                    console.error("公開ラベル取得エラー:", err);
                }

                setUseKml(!!projectData.use_kml);
                if (projectData.use_kml && projectData.kml_data) {
                    try {
                        const geoJsonData = await fetchPublicProjectKmlData(publicUrl);
                        setKmlData(geoJsonData);
                    } catch (err) {
                        console.error("KMLデータ取得エラー:", err);
                    }
                }

                const formattedLocations = locationsData.map(formatPin);
                setArchiveLocations(formattedLocations);
                setLabels(labelsData);
            } catch (err) {
                console.error("公開プロジェクト取得エラー:", err);
                setError("プロジェクトが見つからないか、公開されていません。");
            } finally {
                setLoading(false);
            }
        };

        loadPublicProject();

        // 60秒ごとに公開状態をチェック
        const intervalId = setInterval(checkPublicStatus, 60000);

        // クリーンアップ関数
        return () => {
            clearInterval(intervalId);
        };
    }, [publicUrl, checkPublicStatus]);

    // イントロアニメーション完了時のコールバック
    const handleIntroAnimationComplete = () => {
        setShowIntroAnimation(false);
    };

    // サムネイル画像のURLを取得（thumbnail_image_urlを優先、なければthumbnail_imageを使用）
    const thumbnailUrl = project?.thumbnail_image_url || project?.thumbnail_image;

    if (loading) {
        return <Loading isLoading fullscreen message="地図の読み込み中..." />;
    }

    if (error) {
        return (
            <div className={`flex h-screen items-center justify-center bg-bg-primary text-error ${typeShell.body}`}>
                <p className="m-0">{error}</p>
            </div>
        );
    }

    if (!center) {
        return (
            <div className={`flex h-screen items-center justify-center bg-bg-primary text-error ${typeShell.body}`}>
                <p className="m-0">地図の表示に必要な情報が不足しています。</p>
            </div>
        );
    }

    return (
        <div className={publicMapShell.body}>
            {/* イントロアニメーション */}
            <PublicIntroAnimation
                project={project}
                thumbnailUrl={thumbnailUrl}
                isVisible={showIntroAnimation}
                onComplete={handleIntroAnimationComplete}
            />

            <div className={publicMapShell.header}>
                <div className={publicMapShell.headerContent}>
                    <div className={publicMapShell.headerLeft}>
                        <h1 className={publicMapShell.title}>{project?.name}</h1>
                    </div>
                    {/* 説明文がある場合のみ説明ボタンを表示 */}
                    {/* 常に表示して、説明がない場合はデフォルトメッセージを表示 */}
                    <button
                        className={publicMapShell.descriptionButton}
                        onClick={() => setShowDescriptionModal(true)}
                        aria-label="プロジェクトの説明を表示"
                    >
                        <span className={publicMapShell.descriptionIcon}>info</span>
                        <span className={publicMapShell.descriptionText}>説明</span>
                    </button>
                </div>
            </div>

            <div className={publicMapShell.mapContainer}>
                <MapContainer
                    defaultCenter={center}
                    defaultZoom={zoom}
                    archiveLocations={archiveLocations}
                    labels={labels}
                    isReadOnly={true}
                    projectId={project?.id}
                    publicUrl={publicUrl}
                    kmlData={kmlData}
                    useKml={useKml}
                    kmlTitle={project?.kml_data?.title || null}
                />
            </div>

            {/* 説明文がある場合のみモーダルを表示 */}
            {project?.description && project.description.trim() !== '' && (
                <ProjectDescriptionModal
                    isOpen={showDescriptionModal}
                    onClose={() => setShowDescriptionModal(false)}
                    project={project}
                />
            )}
        </div>
    );
}

export default PublicMapPage;
